/**
 * Hop: skip counting along a road, with the pattern it hides drawn underneath.
 *
 * Hopping by 3s leaves footprints on 3, 6, 9, 12 — and joining up the last
 * digit of each landing (3, 6, 9, 2, 5, 8, 1...) draws a ten-pointed star. The
 * star is different for every step size, the same for 3 and 7, and a single
 * line for 5. That is the times tables as a shape, which is a thing worth
 * seeing years before anyone asks you to recite them.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, Play, RotateCcw, Square } from 'lucide-preact';
import { token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { groupDigits } from '../../lib/bignum.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const STEPS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 25];
/** How many stones of the road are on screen at once. */
const ROAD_LENGTH = 24;

export function SkipToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hopRef = useRef(0);
  const autoRef = useRef(0);

  const [step, setStep] = useState(3);
  const [count, setCount] = useState(0);
  const [auto, setAuto] = useState(false);

  const live = useRef({ step, count });
  live.current = { step, count };

  const at = count * step;

  const hop = () => {
    setCount((c) => c + 1);
    hopRef.current = 1;
    // Each landing sounds a step higher, so counting is heard as well as seen.
    play('count', Math.min(1, ((live.current.count + 1) % 8) / 8 + 0.2));
  };

  /**
   * Hopping by itself keeps time along the road one number per beat, and only
   * sounds a note when it lands on a multiple. That is what turns the pattern
   * into a rhythm you can hear: hopping by 3 against a bar of 4 puts the note
   * somewhere different every bar, which is the same fact as the star being a
   * star rather than a straight line.
   */
  useEffect(() => {
    if (!auto) return;
    let beat = live.current.count * live.current.step;
    const timer = setInterval(() => {
      beat += 1;
      const k = live.current.step;
      if (beat % 4 === 1) play('tap', 0.28);
      if (beat % k === 0) {
        setCount(beat / k);
        hopRef.current = 1;
        play('count', Math.min(1, ((beat / k) % 8) / 8 + 0.2));
      }
    }, 300);
    return () => clearInterval(timer);
  }, [auto]);

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      hopRef.current = Math.max(0, hopRef.current - dt * 2.6);
      autoRef.current += dt;
      const { step: k, count: n } = live.current;
      const landed = n * k;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // ---- the road ------------------------------------------------------
      const roadY = height * 0.3;
      const first = Math.max(0, landed - Math.floor(ROAD_LENGTH * 0.45));
      const pitch = width / ROAD_LENGTH;

      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, roadY + 26);
      ctx.lineTo(width, roadY + 26);
      ctx.stroke();
      ctx.restore();

      for (let i = 0; i < ROAD_LENGTH; i++) {
        const value = first + i;
        const x = i * pitch + pitch / 2;
        const onIt = value > 0 && value % k === 0;
        const visited = onIt && value <= landed;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (visited) {
          ctx.fillStyle = token('--crayon-teal');
          ctx.beginPath();
          ctx.arc(x, roadY + 26, Math.min(pitch * 0.42, 17), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = visited ? '#ffffff' : onIt ? token('--ink') : token('--ink-faint');
        ctx.font = `${visited ? 800 : 600} ${Math.min(pitch * 0.5, 15)}px ui-rounded, system-ui, sans-serif`;
        ctx.fillText(String(value), x, roadY + 26);
        ctx.restore();
      }

      // The hopper, arcing between stones.
      const hopper = ((landed - first) * pitch + pitch / 2) || pitch / 2;
      const lift = Math.sin(hopRef.current * Math.PI) * 46;
      ctx.save();
      ctx.fillStyle = token('--crayon-orange');
      ctx.strokeStyle = 'rgba(44, 37, 69, 0.8)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(hopper, roadY - 12 - lift, 16, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // ---- the star the last digits draw ---------------------------------
      const cx = width / 2;
      const cy = height * 0.68;
      const radius = Math.min(width * 0.32, height * 0.2);
      const pointAt = (digit: number) => {
        const a = (digit / 10) * Math.PI * 2 - Math.PI / 2;
        return { x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius };
      };

      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();

      // Join each landing's last digit to the next one's.
      ctx.strokeStyle = token('--crayon-purple');
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 1; i <= Math.min(n, 40); i++) {
        const from = pointAt((i - 1) * k % 10);
        const to = pointAt((i * k) % 10);
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
      }
      ctx.stroke();

      for (let digit = 0; digit < 10; digit++) {
        const p = pointAt(digit);
        const touched = n > 0 && Array.from({ length: n + 1 }, (_, i) => (i * k) % 10).includes(digit);
        ctx.fillStyle = touched ? token('--crayon-purple') : token('--ink-faint');
        ctx.beginPath();
        ctx.arc(p.x, p.y, touched ? 9 : 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = token('--ink-soft');
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `700 12px ui-rounded, system-ui, sans-serif`;
        const label = pointAt(digit);
        ctx.fillText(String(digit), cx + (label.x - cx) * 1.24, cy + (label.y - cy) * 1.24);
      }
      ctx.restore();
    },

    onDown: () => hop(),
  });

  const reset = () => {
    setCount(0);
    setAuto(false);
    play('whoosh', 0.6);
  };

  const sequence = Array.from({ length: Math.min(count, 6) }, (_, i) => (count - Math.min(count, 6) + i + 1) * step);

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Skip counting road" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label={auto ? 'Stop hopping' : 'Hop by itself'}
              hideLabel
              onClick={() => setAuto((v) => !v)}
            >
              {auto ? <Square size={22} strokeWidth={2.5} /> : <Play size={24} strokeWidth={2.5} />}
            </ToyButton>
            <ToyButton round label="Back to the start" hideLabel onClick={reset}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>{groupDigits(BigInt(at))}</strong>
          <span>{count === 0 ? `tap to hop by ${step}` : sequence.join(', ')}</span>
        </p>

        <Coach>{count > 9 ? 'Look at the shape the last digits make' : 'Tap the paper to hop'}</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="How big a hop">
          {STEPS.map((n) => (
            <Chip
              key={n}
              label={String(n)}
              pressed={step === n}
              onClick={() => {
                setStep(n);
                setCount(0);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
